// Pinned-strip persistence — extracted from index.tsx (#339) so the view no
// longer touches localStorage for these keys.
//
// Pinned strip (Prof, Jul 9): the thumbtack on a position/order copies it into
// a dedicated row between Open Positions and Open Orders. Pins are WATCH
// COPIES — the original stays in its normal sorted/filtered slot and both
// render from the same live sources. Keyed by stable identity (the card VM
// keys: position = ticker+side, order = venue order id) so a pin survives
// sorting, filtering, and refreshes. `label` is snapshotted at pin time so an
// item that leaves the open set can still name itself in its tombstone row.
// The entry type + the key algebra that lets a pin follow an order through a
// cancel+place edit live in ./pinIdentity (#188). This module owns only the
// storage: the entries array and the strip's collapsed flag.
//
// No version suffix and no migration: per-entry validation absorbs shape
// drift the same way filterPersistence does. A malformed entry is dropped
// individually, a corrupt blob falls back to [], and unknown extra fields on
// a valid entry ride along untouched.
import type { PinnedEntry } from './pinIdentity';

export const TRACKER_PINS_KEY = 'g1mt_tracker_pins';
export const TRACKER_PINS_COLLAPSED_KEY = 'g1mt_tracker_pins_collapsed';

export function readPinnedEntries(): PinnedEntry[] {
  try {
    const raw = localStorage.getItem(TRACKER_PINS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is PinnedEntry =>
        !!e &&
        typeof e === 'object' &&
        typeof (e as PinnedEntry).key === 'string' &&
        ((e as PinnedEntry).kind === 'position' || (e as PinnedEntry).kind === 'order') &&
        typeof (e as PinnedEntry).label === 'string',
    );
  } catch {
    return [];
  }
}

export function writePinnedEntries(entries: readonly PinnedEntry[]): void {
  try {
    localStorage.setItem(TRACKER_PINS_KEY, JSON.stringify(entries));
  } catch {
    // Quota / private mode — dropping the write is acceptable for a
    // display-only preference; the pins still work for this session.
  }
}

export function readPinsCollapsed(): boolean {
  try {
    return localStorage.getItem(TRACKER_PINS_COLLAPSED_KEY) === 'true';
  } catch {
    // Safari private mode — the strip starts expanded.
    return false;
  }
}

export function writePinsCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(TRACKER_PINS_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Quota / private mode — collapse still works for this session.
  }
}
